import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from '../../icons/wp-icons';

const shortcuts = [
  { category: 'Navigation', items: [
    { keys: ['?'], description: 'Tastenkürzel anzeigen' },
    { keys: ['⌘', 'K'], description: 'Suche öffnen' },
    { keys: ['Esc'], description: 'Modal/Popup schließen' },
  ]},
  { category: 'Aktionen', items: [
    { keys: ['K'], description: 'Neuer Kunde' },
    { keys: ['N'], description: 'Neue Anfrage (in Kundenansicht)' },
    { keys: ['T'], description: 'Neuer Termin (in Kundenansicht)' },
    { keys: ['E'], description: 'Bearbeiten' },
  ]},
  { category: 'Speichern', items: [
    { keys: ['⌘', 'S'], description: 'Speichern (in Formularen)' },
  ]},
];

function KeyboardShortcutsModal({ isOpen, onClose }) {
  // Escape-Taste zum Schließen
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"
          >
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-light rounded-lg">
                    <Keyboard className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Tastenkürzel</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                {shortcuts.map((section, idx) => (
                  <div key={section.category} className={idx > 0 ? 'mt-5' : ''}>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      {section.category}
                    </h3>
                    <div className="space-y-2">
                      {section.items.map((item, itemIdx) => (
                        <div
                          key={itemIdx}
                          className="flex items-center justify-between py-2"
                        >
                          <span className="text-sm text-gray-600">{item.description}</span>
                          <div className="flex items-center gap-1">
                            {item.keys.map((key, keyIdx) => (
                              <span key={keyIdx}>
                                <kbd className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-md shadow-sm min-w-[28px] inline-flex items-center justify-center">
                                  {key}
                                </kbd>
                                {keyIdx < item.keys.length - 1 && (
                                  <span className="text-gray-400 mx-0.5">+</span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  Drücke <kbd className="px-1.5 py-0.5 text-xs bg-white border border-gray-200 rounded">?</kbd> um diese Hilfe zu öffnen
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default KeyboardShortcutsModal;
